// src/components/About.js
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import HamburgerMenu from "./HamburgerMenu";
import PageHeader from "./PageHeader";
import { fetchWithAuth } from "../utils/api";
import "./Profile.css";

export default function About({ onLogout }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_URL;

    const [form, setForm] = useState({ name: "", coordinates: "", canton: "", description: "" });
    const [sending, setSending] = useState(false);
    const [feedback, setFeedback] = useState({ satisfaction: null, recommend: "", message: "" });
    const [sendingFb, setSendingFb] = useState(false);
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState(
        ["project", "support", "suggest", "feedback"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "project"
    ); // pills : project | support | suggest | feedback

    const submitSuggestion = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.coordinates.trim()) return;
        try {
            setSending(true);
            const res = await fetchWithAuth(`${apiUrl}/suggest-site`, {
                method: "POST",
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error("send failed");
            toast.success(t('suggestSite.success'));
            setForm({ name: "", coordinates: "", canton: "", description: "" });
        } catch (err) {
            console.error(err);
            toast.error(t('suggestSite.error'));
        } finally {
            setSending(false);
        }
    };

    const submitFeedback = async (e) => {
        e.preventDefault();
        if (!feedback.message.trim()) return;
        try {
            setSendingFb(true);
            const res = await fetchWithAuth(`${apiUrl}/feedback`, {
                method: "POST",
                body: JSON.stringify(feedback),
            });
            if (!res.ok) throw new Error("send failed");
            toast.success(t('feedback.success'));
            setFeedback({ satisfaction: null, recommend: "", message: "" });
        } catch (err) {
            console.error(err);
            toast.error(t('feedback.error'));
        } finally {
            setSendingFb(false);
        }
    };

    const inputStyle = { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc", boxSizing: "border-box", fontSize: 14, fontFamily: "inherit" };

    return (
        <div className="profile-container about-page" style={{ position: "relative" }}>
            {/* Bouton fermer (revenir en arrière) */}
            <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label={t('common.close')}
                title={t('common.close')}
                style={{ position: "absolute", top: 15, right: 52, zIndex: 1500, background: "#fff", border: "1px solid #e0e0e0", borderRadius: "50%", width: 34, height: 34, cursor: "pointer", fontSize: 18, color: "#555", lineHeight: 1 }}
            >
                ✕
            </button>

            <div style={{ position: "absolute", top: 15, right: 10, zIndex: 1500 }}>
                <HamburgerMenu
                    onLogout={onLogout}
                    onProfileClick={() => navigate("/profile")}
                    onAboutClick={() => navigate("/about")}
                    onMapClick={() => navigate("/")}
                    onCommunityClick={() => navigate("/community")}
                />
            </div>

            <div className="page-header-bleed">
                <PageHeader title={t('about.title')} />
            </div>

            <div>
                {/* Pills */}
                <div style={{ display: "flex", gap: 6, margin: "6px 0 12px", flexWrap: "nowrap" }}>
                    {[
                        ["project", t('about.tabs.project')],
                        ["support", t('about.tabs.support')],
                        ["suggest", t('about.tabs.suggest')],
                        ["feedback", t('about.tabs.feedback')],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            style={{
                                flex: "1 1 0", minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                borderRadius: 999, padding: "9px 8px", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer",
                                border: tab === key ? "1px solid var(--primary-blue)" : "1px solid #dce6ee",
                                background: tab === key ? "var(--primary-blue)" : "#fff",
                                color: tab === key ? "#fff" : "#55707e",
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {tab === "project" && (
                <div className="profile-section">
                    <p style={{ lineHeight: 1.6, marginTop: 10 }}>{t('about.intro.paragraph1')}</p>
                    <p style={{ lineHeight: 1.6, marginTop: 10 }}>{t('about.intro.paragraph2')}</p>
                    <p style={{ lineHeight: 1.6, marginTop: 10 }}>{t('about.intro.paragraph3')}</p>
                    <p style={{ lineHeight: 1.6, marginTop: 10 }}>{t('about.intro.paragraph4')}</p>

                    <p style={{ fontSize: 13, marginTop: 14 }}>
                        <a
                            href="https://www.swissinfo.ch/fre/societe/plong%c3%a9es-en-eaux-hivernales-dans-les-lacs-suisses/41193192"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#8a97a3", textDecoration: "underline" }}
                        >
                            {t('about.intro.source')}
                        </a>
                    </p>

                    <div style={{ marginTop: 8, display: "flex", gap: 16, flexWrap: "wrap", fontSize: 13 }}>
                        <a href="/cgu" target="_blank" rel="noopener noreferrer" style={{ color: "#8a97a3", textDecoration: "underline" }}>
                            {t('terms.title')}
                        </a>
                        <a href="/confidentialite" target="_blank" rel="noopener noreferrer" style={{ color: "#8a97a3", textDecoration: "underline" }}>
                            {t('privacy.title')}
                        </a>
                    </div>

                    <p style={{ marginTop: 20, textAlign: "right" }}>
                        <button
                            type="button"
                            onClick={() => navigate("/profile/f94d34de-51c6-4c5f-867e-4cfdbd6549b1")}
                            style={{ background: "none", border: "none", padding: 0, color: "var(--primary-blue)", fontWeight: 600, cursor: "pointer", fontSize: 14, textDecoration: "underline", fontFamily: "var(--font-display)" }}
                        >
                            Jeremy Som
                        </button>
                    </p>
                </div>
                )}

                {tab === "support" && (
                <div className="profile-section">
                    <h2 style={{ fontSize: "1.3rem" }}>{t('about.support.title')}</h2>
                    <p style={{ color: "#555", fontSize: 14, lineHeight: 1.5 }}>{t('about.support.intro')}</p>

                    {[
                        {
                            head: t('about.support.recurring'),
                            items: [
                                [t('about.support.domain'), "~15 CHF / an"],
                                [t('about.support.hosting'), "~18 CHF / mois"],
                                [t('about.support.apple'), "99 $ / an"],
                                [t('about.support.other'), t('about.support.free')],
                            ],
                        },
                        {
                            head: t('about.support.oneTime'),
                            items: [
                                [t('about.support.play'), "25 $"],
                            ],
                        },
                    ].map((group, gi) => (
                        <div key={gi}>
                            <h4 style={{ fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", color: "#8a97a3", margin: "14px 0 2px" }}>{group.head}</h4>
                            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 4px", fontSize: 14 }}>
                                {group.items.map(([label, val], i) => (
                                    <li key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "7px 0", borderBottom: "1px solid #eef2f4" }}>
                                        <span style={{ color: "#334" }}>{label}</span>
                                        <span style={{ color: "#55606a", fontWeight: 600, whiteSpace: "nowrap" }}>{val}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    <p style={{ color: "#555", fontSize: 14, lineHeight: 1.5, marginTop: 14 }}>
                        {t('about.support.donateText')}
                    </p>
                    <a
                        href="https://paypal.me/swissdiveagenda"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "inline-block", marginTop: 6, background: "var(--primary-blue)", color: "#fff", textDecoration: "none", fontWeight: 700, borderRadius: 10, padding: "10px 18px" }}
                    >
                        {t('about.support.donateButton')}
                    </a>
                    <p style={{ color: "#8a97a3", fontSize: 13, lineHeight: 1.5, marginTop: 14 }}>
                        {t('about.support.totemPerk')}
                    </p>
                </div>
                )}

                {tab === "suggest" && (
                <div className="profile-section">
                    <h2 style={{ fontSize: "1.3rem" }}>{t('suggestSite.title')}</h2>
                    <p style={{ color: "#555", fontSize: 14, lineHeight: 1.5 }}>{t('suggestSite.intro')}</p>
                    <form onSubmit={submitSuggestion} style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
                        <input style={inputStyle} placeholder={t('suggestSite.name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                        <div>
                            <input style={inputStyle} placeholder={t('suggestSite.coordinates')} value={form.coordinates} onChange={(e) => setForm({ ...form, coordinates: e.target.value })} required />
                            <div style={{ fontSize: 12, color: "#999", marginTop: 3 }}>{t('suggestSite.coordinatesHint')}</div>
                        </div>
                        <input style={inputStyle} placeholder={t('suggestSite.canton')} value={form.canton} onChange={(e) => setForm({ ...form, canton: e.target.value })} />
                        <textarea style={{ ...inputStyle, resize: "vertical" }} rows={3} placeholder={t('suggestSite.description')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button type="submit" disabled={sending} style={{ background: "var(--primary-blue)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontWeight: 700, fontSize: 14, cursor: sending ? "not-allowed" : "pointer", opacity: sending ? 0.6 : 1 }}>
                                {t('suggestSite.submit')}
                            </button>
                        </div>
                    </form>
                </div>
                )}

                {tab === "feedback" && (
                <div className="profile-section">
                    <form onSubmit={submitFeedback} style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}>
                        {/* Satisfaction 1-10 */}
                        <div>
                            <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#55707e", marginBottom: 6 }}>{t('feedback.satisfLabel')}</label>
                            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                                    <button
                                        type="button" key={n}
                                        onClick={() => setFeedback({ ...feedback, satisfaction: n })}
                                        style={{
                                            width: 33, height: 33, borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13,
                                            border: feedback.satisfaction === n ? "1px solid var(--primary-blue)" : "1px solid #dce6ee",
                                            background: feedback.satisfaction === n ? "var(--primary-blue)" : "#fff",
                                            color: feedback.satisfaction === n ? "#fff" : "#55707e",
                                        }}
                                    >{n}</button>
                                ))}
                            </div>
                        </div>
                        {/* Recommandation */}
                        <div>
                            <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#55707e", marginBottom: 6 }}>{t('feedback.recommendLabel')}</label>
                            <div style={{ display: "flex", gap: 6 }}>
                                {[["yes", t('feedback.recommendYes')], ["maybe", t('feedback.recommendMaybe')], ["no", t('feedback.recommendNo')]].map(([v, l]) => (
                                    <button
                                        type="button" key={v}
                                        onClick={() => setFeedback({ ...feedback, recommend: v })}
                                        style={{
                                            flex: "1 1 0", borderRadius: 999, padding: "8px 6px", fontSize: 13, fontWeight: 700, cursor: "pointer",
                                            border: feedback.recommend === v ? "1px solid var(--primary-blue)" : "1px solid #dce6ee",
                                            background: feedback.recommend === v ? "var(--primary-blue)" : "#fff",
                                            color: feedback.recommend === v ? "#fff" : "#55707e",
                                        }}
                                    >{l}</button>
                                ))}
                            </div>
                        </div>
                        {/* Message libre */}
                        <div>
                            <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#55707e", marginBottom: 6 }}>{t('feedback.messageLabel')}</label>
                            <textarea
                                style={{ ...inputStyle, resize: "vertical" }} rows={4} required
                                placeholder={t('feedback.messagePlaceholder')}
                                value={feedback.message}
                                onChange={(e) => setFeedback({ ...feedback, message: e.target.value })}
                            />
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button type="submit" disabled={sendingFb} style={{ background: "var(--primary-blue)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontWeight: 700, fontSize: 14, cursor: sendingFb ? "not-allowed" : "pointer", opacity: sendingFb ? 0.6 : 1 }}>
                                {t('feedback.submit')}
                            </button>
                        </div>
                    </form>
                </div>
                )}

                <div className="profile-bottom-spacer" />
            </div>

            <ToastContainer position="bottom-center" autoClose={3000} theme="colored" />
        </div>
    );
}
